import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useModal } from '@/contexts/modal'
import { useConfirmation } from '@/contexts/confirmation'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import useQueryClientCallback from '@/hooks/useQueryClientCallback'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import type { ApplyPriceChangeModalProps } from '@/modals/ServiceModals/ApplyPriceChangeModal'
import {
    getServicePriceChangeRequests,
    rejectPriceChangeRequest,
} from '@/modules/Service/services'
import { useTranslation } from 'react-i18next'
import { Button, Typogaphy } from 'vnetwork-ui'
import { AlertTriangle, Check, X } from 'lucide-react'

/** Banner shown in Information tab when service has pending price change requests */
const PriceChangeAlert = ({ serviceID }: { serviceID: number }) => {
    const { t } = useTranslation('services')
    const { setModal } = useModal()
    const { setConfirmation } = useConfirmation()

    const { data: allRequests = [] } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.SERVICES_PRICE_CHANGE_REQUESTS, serviceID],
        queryFn: () => getServicePriceChangeRequests(serviceID),
    })

    const requests = allRequests.filter((r) => r.status === 'pending')

    const { refetch } = useQueryClientCallback({
        reactQueryKeys: [QUERY_KEYS.SERVICES_PRICE_CHANGE_REQUESTS],
    })

    const { mutateAsync: rejectMutation } = useMutationWithHandler(
        { mutationFn: rejectPriceChangeRequest },
        {
            successMessage: t('price_change_requests.reject_success'),
            reactQueryKeys: QUERY_KEYS.SERVICES_PRICE_CHANGE_REQUESTS,
            onSuccess: () => refetch(),
        }
    )

    if (!requests.length) return null

    return (
        <div className="border-l-2 border-amber-400 bg-background px-3 py-2 space-y-2">
            <Typogaphy className="text-xs text-muted-foreground font-medium flex items-center gap-1.5">
                <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
                {t('price_change_requests.title')} ({requests.length})
            </Typogaphy>
            {requests.map((req) => (
                <div
                    key={req.id}
                    className="flex items-center justify-between text-sm"
                >
                    <div className="flex items-center gap-3">
                        <span className="text-xs text-muted-foreground capitalize">
                            {req.price_type === 'recurring'
                                ? t('prices.recurring')
                                : t('prices.register')}
                        </span>
                        <span className="text-xs text-red-500 line-through">
                            {req.old_price?.toLocaleString()}
                        </span>
                        <span className="text-muted-foreground text-xs">→</span>
                        <span className="text-xs text-green-600 font-medium">
                            {req.new_price?.toLocaleString()}
                        </span>
                    </div>
                    <div className="flex items-center gap-1">
                        <Button
                            variant="text"
                            size="small"
                            color="success"
                            onClick={() =>
                                setModal<ApplyPriceChangeModalProps>(
                                    'ServiceModals/ApplyPriceChangeModal/index',
                                    { priceChangeRequest: req }
                                )
                            }
                        >
                            <Check className="w-3 h-3 mr-0.5" />
                            {t('price_change_requests.apply')}
                        </Button>
                        <Button
                            variant="text"
                            size="small"
                            color="error"
                            onClick={() =>
                                setConfirmation({
                                    open: true,
                                    title: t(
                                        'price_change_requests.reject_title'
                                    ),
                                    description: t(
                                        'price_change_requests.reject_description',
                                        {
                                            service_name: req.service_name,
                                        }
                                    ),
                                    onConfirm: () =>
                                        rejectMutation({ id: req.id }),
                                })
                            }
                        >
                            <X className="w-3 h-3 mr-0.5" />
                            {t('price_change_requests.reject')}
                        </Button>
                    </div>
                </div>
            ))}
        </div>
    )
}

export default PriceChangeAlert
